/**
 * EPL Admin Dashboard - Vista Principal
 * KPIs y estadísticas generales del sistema
 */

import React from 'react';
import { 
  Users, 
  FlaskConical, 
  CheckCircle, 
  TrendingUp,
  Brain,
  DollarSign,
  Clock,
  Award,
  Loader
} from 'lucide-react';
import { useEplAdminDashboard } from '../../hooks/useEplAdmin';

const AdminDashboard = () => {
  const { stats, recentActivity, loading, error, actions } = useEplAdminDashboard();

  const StatCard = ({ icon: Icon, label, value, subtitle, trend, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      indigo: 'bg-indigo-50 text-indigo-600',
      teal: 'bg-teal-50 text-teal-600'
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-4`}>
              <Icon className="w-6 h-6" />
            </div>
            <p className="text-sm text-gray-600 mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm ${
              trend > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span>{Math.abs(trend)}%</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-40 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h2>
          <p className="text-gray-600">Visión general del sistema EPL Lab Builder</p>
        </div>
        <button
          onClick={actions.refresh}
          disabled={loading}
          className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader className="w-4 h-4 animate-spin" />}
          Actualizar
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-semibold">Error al cargar datos:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          label="Total Usuarios"
          value={loading ? '...' : stats.totalUsers}
          subtitle="Activos en la plataforma"
          color="blue"
        />
        
        <StatCard
          icon={FlaskConical}
          label="Laboratorios"
          value={loading ? '...' : stats.totalLabs}
          subtitle="Labs completados"
          color="purple"
        />
        
        <StatCard
          icon={CheckCircle}
          label="Evaluaciones"
          value={loading ? '...' : stats.totalEvaluations}
          subtitle="Total completadas"
          color="green"
        />
        
        <StatCard
          icon={Award}
          label="Score Promedio"
          value={loading ? '...' : `${stats.avgScore}%`}
          subtitle="Rendimiento general"
          color="orange"
        />
      </div>

      {/* AI Analytics Stats */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Analíticas de IA</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Brain}
            label="Evaluaciones IA"
            value={loading ? '...' : stats.aiEvaluations}
            subtitle="Procesadas con OpenAI"
            color="indigo"
          />
          
          <StatCard
            icon={DollarSign}
            label="Tokens Consumidos"
            value={loading ? '...' : `${(stats.tokensUsed / 1000).toFixed(0)}K`}
            subtitle="Total de tokens usados"
            color="teal"
          />
          
          <StatCard
            icon={Clock}
            label="Tiempo Respuesta"
            value={loading ? '...' : `${stats.avgResponseTime}s`}
            subtitle="Promedio de IA"
            color="blue"
          />
          
          <StatCard
            icon={Users}
            label="Activos Hoy"
            value={loading ? '...' : stats.activeToday}
            subtitle="Usuarios conectados"
            color="green"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="bg-white rounded-lg border border-gray-200">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />
              Cargando actividad...
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hay actividad reciente
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentActivity.map((activity, idx) => (
                <li key={idx} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {activity.user.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                        <p className="text-xs text-gray-500">{activity.action}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">Score: {activity.score}%</p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.time).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Chart 1 - Evaluaciones por fecha */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Evaluaciones por Fecha</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Gráfico de línea - Próximamente</p>
          </div>
        </div>

        {/* Chart 2 - Labs por sistema */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Labs por Sistema</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Gráfico de torta - Próximamente</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Juan Pérez completó "BML Canvas"</p>
              <p className="text-xs text-gray-500">Hace 5 minutos</p>
            </div>
            <span className="text-sm font-semibold text-green-600">85/100</span>
          </div>
          
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Prof. Ana creó "Validación de Ideas"</p>
              <p className="text-xs text-gray-500">Hace 15 minutos</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">3 nuevos usuarios registrados</p>
              <p className="text-xs text-gray-500">Hace 1 hora</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
